/**
 * One-shot localStorage migration shim for the 2026-02 CRA3D rebrand.
 *
 * BEFORE:  localStorage["oowa.access_token"]  + localStorage["oowa.draft.*"]
 * AFTER:   localStorage["cra3d.access_token"] + localStorage["cra3d.draft.*"]
 *
 * Behaviour:
 *  - Runs exactly once per browser (guarded by "cra3d.storage_migration_v1").
 *  - For each legacy key it finds: copies value to the new key ONLY if the
 *    new key is empty, then removes the legacy key. If the new key
 *    already carries a fresher value we keep that and drop the legacy
 *    row (no downgrade).
 *  - NEVER logs or displays the migrated values (may include a JWT).
 *  - Safe to call from any module — it aborts early after the first run.
 *  - No user is logged out. No draft is lost.
 */
const MIGRATION_GUARD_KEY = "cra3d.storage_migration_v1";

const LEGACY_PREFIX = "oowa.";
const NEW_PREFIX = "cra3d.";


function _hasLocalStorage() {
  try {
    return typeof window !== "undefined"
        && typeof window.localStorage !== "undefined";
  } catch {
    return false;
  }
}


function _keyMap(legacyKey) {
  if (!legacyKey.startsWith(LEGACY_PREFIX)) return null;
  return NEW_PREFIX + legacyKey.slice(LEGACY_PREFIX.length);
}


/**
 * Migrate any legacy `oowa.*` keys to `cra3d.*` exactly once.
 * Returns { migrated, skipped, already_ran } for diagnostics
 * (safe to log — no values).
 */
export function migrateLegacyStorage() {
  if (!_hasLocalStorage()) return { migrated: 0, skipped: 0, already_ran: false };
  try {
    if (localStorage.getItem(MIGRATION_GUARD_KEY)) {
      return { migrated: 0, skipped: 0, already_ran: true };
    }
    let migrated = 0;
    let skipped = 0;
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const newKey = _keyMap(k);
      if (!newKey) continue;
      const value = localStorage.getItem(k);
      if (value == null) {
        toRemove.push(k);
        continue;
      }
      const existing = localStorage.getItem(newKey);
      if (existing == null || existing === "") {
        localStorage.setItem(newKey, value);
        migrated += 1;
      } else {
        skipped += 1;
      }
      toRemove.push(k);
    }
    for (const k of toRemove) {
      try { localStorage.removeItem(k); } catch { /* ignore */ }
    }
    localStorage.setItem(MIGRATION_GUARD_KEY, new Date().toISOString());
    return { migrated, skipped, already_ran: false };
  } catch {
    // Never let a storage-migration crash the app. Any exception here
    // means the user simply keeps whatever keys their browser has.
    return { migrated: 0, skipped: 0, already_ran: false };
  }
}


export default migrateLegacyStorage;
